import { useEffect, useState } from 'react';
import { apiGet, apiPost, ApiError } from '../lib/apiClient';
import { StatusBadge } from '../components/StatusBadge';
import { DataTable } from '../components/DataTable';
import { useModal } from '../contexts/ModalContext';

interface FxRateResult {
  usdToCopRate: number;
}

interface RefreshFxResult {
  ok: boolean;
  usdToCopRate: number;
}

interface QaStatisticsResult {
  totalReviews: number;
  okCount: number;
  needsReviewCount: number;
  safetyEventCount: number;
}

interface BackupExportResult {
  ok: boolean;
  operationName: string;
  outputUriPrefix: string;
}

interface PlatformAlertPolicy {
  name: string;
  displayName: string;
  enabled: boolean;
  conditionCount: number;
  notificationChannelCount: number;
}

const GRACE_PERIOD_CONFIRM_PHRASE = 'MIGRATE GRACE PERIOD';

export function SystemOpsPage() {
  const { confirm } = useModal();
  const [usdToCopRate, setUsdToCopRate] = useState<number | null>(null);
  const [rateLoading, setRateLoading] = useState(false);
  const [rateError, setRateError] = useState<string | null>(null);

  const [qaStats, setQaStats] = useState<QaStatisticsResult | null>(null);
  const [qaLoading, setQaLoading] = useState(false);
  const [qaError, setQaError] = useState<string | null>(null);

  const [backupResult, setBackupResult] = useState<BackupExportResult | null>(null);
  const [backupLoading, setBackupLoading] = useState(false);
  const [backupError, setBackupError] = useState<string | null>(null);

  const [migrateInput, setMigrateInput] = useState('');
  const [migrateLoading, setMigrateLoading] = useState(false);
  const [migrateError, setMigrateError] = useState<string | null>(null);
  const [migrateResult, setMigrateResult] = useState<unknown>(null);

  const [alertPolicies, setAlertPolicies] = useState<PlatformAlertPolicy[] | null>(null);
  const [alertsLoading, setAlertsLoading] = useState(true);
  const [alertsError, setAlertsError] = useState<string | null>(null);

  useEffect(() => {
    apiGet<PlatformAlertPolicy[]>('/platform-alerts/admin')
      .then(setAlertPolicies)
      .catch((err) =>
        setAlertsError(err instanceof ApiError ? err.message : 'Failed to load alert policies'),
      )
      .finally(() => setAlertsLoading(false));
  }, []);

  const loadRate = async () => {
    setRateLoading(true);
    setRateError(null);
    try {
      const data = await apiGet<FxRateResult>('/fx-rate/admin/current');
      setUsdToCopRate(data.usdToCopRate);
    } catch (err) {
      setRateError(err instanceof ApiError ? err.message : 'Failed to load exchange rate');
    } finally {
      setRateLoading(false);
    }
  };

  const refreshRate = async () => {
    setRateLoading(true);
    setRateError(null);
    try {
      const data = await apiPost<RefreshFxResult>('/fx-rate/admin/refresh');
      setUsdToCopRate(data.usdToCopRate);
    } catch (err) {
      setRateError(err instanceof ApiError ? err.message : 'Failed to refresh exchange rate');
    } finally {
      setRateLoading(false);
    }
  };

  const loadQaStats = async () => {
    setQaLoading(true);
    setQaError(null);
    try {
      const data = await apiGet<QaStatisticsResult>('/qa/statistics');
      setQaStats(data);
    } catch (err) {
      setQaError(err instanceof ApiError ? err.message : 'Failed to load QA statistics');
    } finally {
      setQaLoading(false);
    }
  };

  const triggerBackup = async () => {
    const ok = await confirm({
      title: 'Trigger Firestore backup',
      message: 'Manually trigger a Firestore export now? This starts a real export operation.',
      confirmLabel: 'Trigger backup',
      danger: true,
    });
    if (!ok) return;
    setBackupLoading(true);
    setBackupError(null);
    setBackupResult(null);
    try {
      const data = await apiPost<BackupExportResult>('/backup/admin/export');
      setBackupResult(data);
    } catch (err) {
      setBackupError(err instanceof ApiError ? err.message : 'Backup export failed');
    } finally {
      setBackupLoading(false);
    }
  };

  const triggerGracePeriodMigration = async () => {
    const ok = await confirm({
      title: 'Run grace-period migration',
      message: 'Run the one-time grace-period migration now? Type-to-confirm has already been satisfied — this will call the backend migration endpoint.',
      confirmLabel: 'Run migration',
      danger: true,
    });
    if (!ok) return;
    setMigrateLoading(true);
    setMigrateError(null);
    setMigrateResult(null);
    try {
      const data = await apiPost<unknown>('/verification/admin/migrate-grace-period');
      setMigrateResult(data);
      setMigrateInput('');
    } catch (err) {
      setMigrateError(err instanceof ApiError ? err.message : 'Grace period migration failed');
    } finally {
      setMigrateLoading(false);
    }
  };

  return (
    <div>
      <div className="section-header">
        <div>
          <h1>System Ops</h1>
          <p>Exchange rate management, QA statistics, and one-time administrative operations.</p>
        </div>
      </div>

      <div className="card-grid" style={{ marginBottom: '1.5rem' }}>
        <div className="form-card">
          <h2>Exchange Rate</h2>
          {rateLoading ? (
            <p className="empty-state">Loading exchange rate…</p>
          ) : rateError ? (
            <div className="error-banner">{rateError}</div>
          ) : usdToCopRate !== null ? (
            <>
              <p style={{ margin: 0 }}>Current USD → COP rate: <strong>{usdToCopRate}</strong></p>
              <button type="button" className="btn btn-primary" disabled={rateLoading} onClick={() => void refreshRate()}>
                {rateLoading ? 'Refreshing…' : 'Refresh Rate'}
              </button>
            </>
          ) : (
            <button type="button" className="btn btn-primary" onClick={() => void loadRate()}>Load Exchange Rate</button>
          )}
        </div>

        <div className="form-card">
          <h2>QA Statistics</h2>
          {qaLoading ? (
            <p className="empty-state">Loading QA statistics…</p>
          ) : qaError ? (
            <div className="error-banner">{qaError}</div>
          ) : qaStats ? (
            <div className="stat-strip" style={{ marginTop: '0.5rem' }}>
              <div className="stat-strip-item">
                <span className="stat-strip-label">Total reviews</span>
                <span className="stat-strip-value">{qaStats.totalReviews}</span>
              </div>
              <div className="stat-strip-item">
                <span className="stat-strip-label">OK</span>
                <span className="stat-strip-value">{qaStats.okCount}</span>
              </div>
              <div className="stat-strip-item">
                <span className="stat-strip-label">Needs review</span>
                <span className="stat-strip-value">{qaStats.needsReviewCount}</span>
                {qaStats.needsReviewCount > 0 && (
                  <span className="stat-strip-meta">Awaiting a reviewer</span>
                )}
              </div>
              <div className="stat-strip-item">
                <span className="stat-strip-label">Safety events</span>
                <span
                  className="stat-strip-value"
                  /* The one figure here that is bad news when it is not zero. */
                  style={qaStats.safetyEventCount > 0 ? { color: 'var(--danger-bold)' } : undefined}
                >
                  {qaStats.safetyEventCount}
                </span>
              </div>
            </div>
          ) : (
            <button type="button" className="btn btn-primary" onClick={() => void loadQaStats()}>Load Statistics</button>
          )}
        </div>
      </div>

      <div className="form-card" style={{ marginBottom: '1.5rem', maxWidth: 'none' }}>
        <h2>Platform Alerts</h2>
        <p className="form-hint" style={{ marginTop: '0.25rem', marginBottom: '0.75rem' }}>
          Read-only mirror of the Cloud Monitoring alert policies configured for this project.
          Edit policies themselves in Cloud Console.
        </p>
        {alertsLoading ? (
          <p className="empty-state">Loading alert policies…</p>
        ) : alertsError ? (
          <div className="error-banner">{alertsError}</div>
        ) : alertPolicies && alertPolicies.length > 0 ? (
          <DataTable
            id="system-ops-alert-policies"
            rows={alertPolicies}
            empty="No alert policies found."
            noun="policies"
            getRowKey={(policy) => policy.name}
            columns={[
              { key: 'policy', header: 'Policy', value: (policy) => policy.displayName },
              {
                key: 'status',
                header: 'Status',
                value: (policy) => (policy.enabled ? 'Enabled' : 'Disabled'),
                filter: 'select',
                render: (policy) => (
                  <StatusBadge
                    label={policy.enabled ? 'Enabled' : 'Disabled'}
                    tone={policy.enabled ? 'good' : 'neutral'}
                  />
                ),
              },
              {
                key: 'conditions',
                header: 'Conditions',
                value: (policy) => policy.conditionCount,
                align: 'right',
              },
              {
                key: 'channels',
                header: 'Notification channels',
                value: (policy) => policy.notificationChannelCount,
                render: (policy) =>
                  policy.notificationChannelCount === 0 ? (
                    <StatusBadge label="No channel — nobody is notified" tone="bad" />
                  ) : (
                    policy.notificationChannelCount
                  ),
              },
            ]}
          />
        ) : (
          <p className="empty-state">No alert policies found.</p>
        )}
      </div>

      <div className="form-card" style={{ marginBottom: '1.5rem', maxWidth: 'none' }}>
        <h2>Observability</h2>
        <p className="form-hint" style={{ marginTop: '0.25rem', marginBottom: '0.75rem' }}>
          The backend has an OpenTelemetry pipeline built (traces + metrics → Cloud Trace / Cloud
          Monitoring via GCP's native OTLP endpoint) but it is currently off in every environment
          — <code>OTEL_ENABLED</code> is unset on all three Cloud Run services. Once enabled,
          traces and metrics show up directly in GCP's own viewers below — no separate portal
          screen needed for this.
        </p>
        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
          <a
            className="btn btn-primary"
            href="https://console.cloud.google.com/traces/list?project=mediclink-bb009"
            target="_blank"
            rel="noreferrer"
          >
            Open Cloud Trace
          </a>
          <a
            className="btn btn-primary"
            href="https://console.cloud.google.com/monitoring/metrics-explorer?project=mediclink-bb009"
            target="_blank"
            rel="noreferrer"
          >
            Open Metrics Explorer
          </a>
        </div>
      </div>

      <div className="form-card" style={{ maxWidth: 'none', borderColor: 'var(--danger)' }}>
        <h2 style={{ color: 'var(--danger)' }}>Danger Zone — manual, non-routine actions</h2>

        <div className="subcard-grid" style={{ marginTop: '0.5rem' }}>
          <div className="subcard">
            <h3>Trigger Firestore Backup</h3>
            {backupError && <div className="error-banner">{backupError}</div>}
            {backupResult && (
              <div className="result-card">
                <p style={{ margin: '0 0 0.25rem' }}><strong>Operation:</strong> {backupResult.operationName}</p>
                <p style={{ margin: 0 }}><strong>Output URI prefix:</strong> {backupResult.outputUriPrefix}</p>
              </div>
            )}
            <button type="button" className="btn btn-danger" disabled={backupLoading} onClick={() => void triggerBackup()}>
              {backupLoading ? 'Triggering…' : 'Trigger Firestore Backup'}
            </button>
          </div>

          <div className="subcard">
            <h3>Migrate Grace Period (one-time)</h3>
            <p className="form-hint" style={{ margin: 0 }}>
              One-time migration: assigns a 30-day GRACE_PERIOD to every existing unverified specialist. Idempotent, but should only be run once after a fresh deployment — do not run this routinely.
            </p>
            {migrateError && <div className="error-banner">{migrateError}</div>}
            {migrateResult !== null && (
              <div className="result-card">
                <pre style={{ whiteSpace: 'pre-wrap', margin: 0, fontSize: '0.8rem' }}>{JSON.stringify(migrateResult, null, 2)}</pre>
              </div>
            )}
            <label htmlFor="gracePeriodConfirmInput">Type the confirmation phrase to enable the button</label>
            <input
              id="gracePeriodConfirmInput"
              value={migrateInput}
              onChange={(e) => setMigrateInput(e.target.value)}
              placeholder="MIGRATE GRACE PERIOD"
            />
            <button
              type="button"
              className="btn btn-danger"
              disabled={migrateLoading || migrateInput.trim() !== GRACE_PERIOD_CONFIRM_PHRASE}
              onClick={() => void triggerGracePeriodMigration()}
            >
              {migrateLoading ? 'Running migration…' : 'Run Grace Period Migration'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
